import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, PencilSimple, Trash, CurrencyDollar } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const PlanManagement = () => {
  const [planos, setPlanos] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState(null);
  const [loading, setLoading] = useState(true);

  const emptyForm = {
    nome: '',
    horas_eletricista: '',
    horas_auxiliar: '',
    preco_semestral: '',
    preco_anual: '',
    beneficios: 'Instalacao Eletrica, Manutencao Eletrica, Climatizacao, Atendimento 24/7'
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchPlanos(); }, []);

  const fetchPlanos = async () => {
    try {
      const res = await api.get('/planos');
      setPlanos(res.data);
    } catch (error) {
      console.error('Error fetching planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/planos', {
        nome: formData.nome,
        horas_eletricista: parseFloat(formData.horas_eletricista),
        horas_auxiliar: parseFloat(formData.horas_auxiliar),
        preco_semestral: parseFloat(formData.preco_semestral),
        preco_anual: parseFloat(formData.preco_anual),
        beneficios: formData.beneficios.split(',').map(b => b.trim()).filter(Boolean)
      });
      setIsCreateOpen(false);
      setFormData(emptyForm);
      fetchPlanos();
    } catch (error) {
      alert('Erro ao criar plano: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedPlano) return;
    try {
      await api.put(`/planos/${selectedPlano.id}`, {
        nome: formData.nome || undefined,
        horas_eletricista: formData.horas_eletricista ? parseFloat(formData.horas_eletricista) : undefined,
        horas_auxiliar: formData.horas_auxiliar ? parseFloat(formData.horas_auxiliar) : undefined,
        preco_semestral: formData.preco_semestral ? parseFloat(formData.preco_semestral) : undefined,
        preco_anual: formData.preco_anual ? parseFloat(formData.preco_anual) : undefined,
        beneficios: formData.beneficios ? formData.beneficios.split(',').map(b => b.trim()).filter(Boolean) : undefined
      });
      setIsEditOpen(false);
      setSelectedPlano(null);
      setFormData(emptyForm);
      fetchPlanos();
    } catch (error) {
      alert('Erro ao atualizar plano: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async () => {
    if (!selectedPlano) return;
    try {
      await api.delete(`/planos/${selectedPlano.id}`);
      setIsDeleteOpen(false);
      setSelectedPlano(null);
      fetchPlanos();
    } catch (error) {
      alert('Erro ao excluir plano: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleActive = async (plano) => {
    try {
      await api.put(`/planos/${plano.id}`, { ativo: !plano.ativo });
      fetchPlanos();
    } catch (error) {
      console.error('Error toggling plan:', error);
    }
  };

  const openEdit = (plano) => {
    setSelectedPlano(plano);
    setFormData({
      nome: plano.nome,
      horas_eletricista: String(plano.horas_eletricista),
      horas_auxiliar: String(plano.horas_auxiliar),
      preco_semestral: String(plano.preco_semestral),
      preco_anual: String(plano.preco_anual),
      beneficios: (plano.beneficios || []).join(', ')
    });
    setIsEditOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-400">Carregando...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
          Gerenciar Planos
        </h2>
        <Button
          onClick={() => { setFormData(emptyForm); setIsCreateOpen(true); }}
          className="bg-[#FFB800] text-black hover:bg-[#E6A600]"
          data-testid="add-plan-button"
        >
          <Plus size={20} className="mr-2" />
          Novo Plano
        </Button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {planos.map(plano => (
          <div
            key={plano.id}
            className={`bg-[#121212] border rounded-sm overflow-hidden transition-all ${
              plano.ativo ? 'border-[#27272A] hover:border-[#FFB800]' : 'border-zinc-800 opacity-60'
            }`}
            data-testid={`plan-card-${plano.nome}`}
          >
            {/* Plan Header */}
            <div className="bg-[#050505] p-6 text-center border-b border-[#27272A]">
              <h3 className="text-2xl font-black text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>
                {plano.nome}
              </h3>
              <div className="text-zinc-400 text-sm mt-1">
                {plano.horas_eletricista}/{plano.horas_auxiliar}
              </div>
              {!plano.ativo && (
                <Badge className="bg-zinc-700 text-zinc-400 mt-2">Inativo</Badge>
              )}
            </div>

            {/* Pricing */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-zinc-500 text-xs mb-1">Semestral</div>
                  <div className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
                    {formatCurrency(plano.preco_semestral)}
                  </div>
                  <div className="text-zinc-500 text-xs">/mes</div>
                </div>
                <div className="text-center">
                  <div className="text-zinc-500 text-xs mb-1">Anual</div>
                  <div className="text-xl font-bold text-emerald-500" style={{ fontFamily: 'Barlow Condensed' }}>
                    {formatCurrency(plano.preco_anual)}
                  </div>
                  <div className="text-zinc-500 text-xs">/mes</div>
                </div>
              </div>

              {/* Hours */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Eletricista</span>
                  <span className="text-white font-semibold">{plano.horas_eletricista}h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Auxiliar</span>
                  <span className="text-white font-semibold">{plano.horas_auxiliar}h</span>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-1 mb-6">
                {plano.beneficios?.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                    {b}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(plano)}
                  className={`flex-1 px-3 py-2 text-xs rounded-sm border transition-colors ${
                    plano.ativo
                      ? 'border-emerald-600 text-emerald-500 hover:bg-emerald-900/20'
                      : 'border-zinc-600 text-zinc-400 hover:bg-zinc-800'
                  }`}
                  data-testid={`toggle-plan-${plano.nome}`}
                >
                  {plano.ativo ? 'Ativo' : 'Inativo'}
                </button>
                <button
                  onClick={() => openEdit(plano)}
                  className="p-2 text-zinc-400 hover:text-[#FFB800] transition-colors border border-[#27272A] rounded-sm"
                  data-testid={`edit-plan-${plano.nome}`}
                >
                  <PencilSimple size={16} />
                </button>
                <button
                  onClick={() => { setSelectedPlano(plano); setIsDeleteOpen(true); }}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors border border-[#27272A] rounded-sm"
                  data-testid={`delete-plan-${plano.nome}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Plano</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Nome do Plano *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                placeholder="Ex: PREMIUM"
                required
                data-testid="plan-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Horas Eletricista *</Label>
                <Input
                  type="number" step="0.5"
                  value={formData.horas_eletricista}
                  onChange={(e) => setFormData({...formData, horas_eletricista: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  required
                  data-testid="plan-horas-eletricista-input"
                />
              </div>
              <div>
                <Label>Horas Auxiliar *</Label>
                <Input
                  type="number" step="0.5"
                  value={formData.horas_auxiliar}
                  onChange={(e) => setFormData({...formData, horas_auxiliar: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  required
                  data-testid="plan-horas-auxiliar-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preco Semestral (R$) *</Label>
                <Input
                  type="number" step="0.01"
                  value={formData.preco_semestral}
                  onChange={(e) => setFormData({...formData, preco_semestral: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  required
                  data-testid="plan-preco-semestral-input"
                />
              </div>
              <div>
                <Label>Preco Anual (R$) *</Label>
                <Input
                  type="number" step="0.01"
                  value={formData.preco_anual}
                  onChange={(e) => setFormData({...formData, preco_anual: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  required
                  data-testid="plan-preco-anual-input"
                />
              </div>
            </div>
            <div>
              <Label>Beneficios (separados por virgula)</Label>
              <Input
                value={formData.beneficios}
                onChange={(e) => setFormData({...formData, beneficios: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                placeholder="Instalacao Eletrica, Manutencao Eletrica..."
                data-testid="plan-beneficios-input"
              />
            </div>
            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="submit-plan-button">
              Criar Plano
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Plano — {selectedPlano?.nome}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label>Nome do Plano</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                data-testid="edit-plan-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Horas Eletricista</Label>
                <Input
                  type="number" step="0.5"
                  value={formData.horas_eletricista}
                  onChange={(e) => setFormData({...formData, horas_eletricista: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  data-testid="edit-plan-horas-eletricista-input"
                />
              </div>
              <div>
                <Label>Horas Auxiliar</Label>
                <Input
                  type="number" step="0.5"
                  value={formData.horas_auxiliar}
                  onChange={(e) => setFormData({...formData, horas_auxiliar: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  data-testid="edit-plan-horas-auxiliar-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preco Semestral (R$)</Label>
                <Input
                  type="number" step="0.01"
                  value={formData.preco_semestral}
                  onChange={(e) => setFormData({...formData, preco_semestral: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  data-testid="edit-plan-preco-semestral-input"
                />
              </div>
              <div>
                <Label>Preco Anual (R$)</Label>
                <Input
                  type="number" step="0.01"
                  value={formData.preco_anual}
                  onChange={(e) => setFormData({...formData, preco_anual: e.target.value})}
                  className="bg-[#050505] border-[#27272A] text-white"
                  data-testid="edit-plan-preco-anual-input"
                />
              </div>
            </div>
            <div>
              <Label>Beneficios (separados por virgula)</Label>
              <Input
                value={formData.beneficios}
                onChange={(e) => setFormData({...formData, beneficios: e.target.value})}
                className="bg-[#050505] border-[#27272A] text-white"
                data-testid="edit-plan-beneficios-input"
              />
            </div>
            <Button type="submit" className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="submit-edit-plan-button">
              Salvar Alteracoes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Excluir Plano</DialogTitle>
          </DialogHeader>
          {selectedPlano && (
            <div className="space-y-4">
              <p className="text-zinc-400">Tem certeza que deseja excluir o plano abaixo?</p>
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm text-center">
                <div className="text-2xl font-black text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>
                  {selectedPlano.nome}
                </div>
                <div className="text-zinc-400 text-sm mt-1">
                  {selectedPlano.horas_eletricista}h eletricista / {selectedPlano.horas_auxiliar}h auxiliar
                </div>
              </div>
              <p className="text-red-400 text-xs">
                Clientes vinculados a este plano nao serao afetados.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => setIsDeleteOpen(false)} className="flex-1 bg-[#1E1E1E] hover:bg-[#27272A]">
                  Cancelar
                </Button>
                <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-delete-plan-button">
                  Excluir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanManagement;
